// src/main.ts

/**
 * Pre-bootstrap header-bloat guard.
 * Runs synchronously BEFORE Angular tries to fetch anything. If cookies have
 * grown past a safe size (commonly caused by stacked AspNetCore.Cookies +
 * OpenIddict + cura_continue across botched redirect attempts), we strip the
 * non-essential ones and reload. Prevents the page from crashing with a
 * Vite/Node "431 Request Header Fields Too Large" before any of our code runs.
 */
(function checkAndCleanBloatedCookies() {
  try {
    const cookieStr = document.cookie || '';
    const SAFE_COOKIE_BYTES = 6 * 1024; // 6 KB — well below the 8 KB header limit
    if (cookieStr.length < SAFE_COOKIE_BYTES) return;

    const SAFE_TO_DROP = [
      'cura_continue',
      'OpenIddict.Server',
      'OpenIddict.Validation',
      '.AspNetCore.OpenIdConnect.Nonce',
      '.AspNetCore.Correlation',
      '.AspNetCore.Antiforgery',
    ];
    const past = new Date(0).toUTCString();
    let cleared = 0;
    for (const c of cookieStr.split(';')) {
      const name = c.split('=')[0]?.trim();
      if (!name) continue;
      if (!SAFE_TO_DROP.some((p) => name.startsWith(p))) continue;
      // Try a few common paths so we delete reliably regardless of where it was set.
      for (const path of ['/', '/Core', '/Auth', '/connect']) {
        document.cookie = `${name}=; expires=${past}; path=${path}`;
      }
      cleared++;
    }

    const stillBloated = (document.cookie || '').length >= SAFE_COOKIE_BYTES;
    if (cleared > 0 || stillBloated) {
      // If we couldn't get under the threshold, nuke EVERYTHING and start fresh.
      if (stillBloated) {
        for (const c of (document.cookie || '').split(';')) {
          const name = c.split('=')[0]?.trim();
          if (!name) continue;
          for (const path of ['/', '/Core', '/Auth', '/connect']) {
            document.cookie = `${name}=; expires=${past}; path=${path}`;
          }
        }
        try { sessionStorage.clear(); } catch {}
      }
      // Reload to drop the bad request headers from the browser cache.
      // Use a marker so we don't reload in a tight loop if cleanup didn't help.
      const KEY = 'auth.cookieCleanupReloadAt';
      const last = Number(sessionStorage.getItem(KEY) || 0);
      if (Date.now() - last > 5000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.replace(window.location.pathname + window.location.search);
      }
    }
  } catch {
    /* never let cookie cleanup break boot */
  }
})();

import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { APP_INITIALIZER, inject, importProvidersFrom } from '@angular/core';
import { AppComponent } from './app/app.component';
import { APP_ROUTES } from './app/app.routes';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AuthInterceptor } from './app/core/auth.interceptor';
import { ErrorInterceptor } from './app/core/error.interceptor';
import { TranslateModule, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { AuthService } from './app/core/auth.service';
import { LanguageService } from './app/core/language.service';

export function initAuthFactory() {
  const auth = inject(AuthService);
  return () => auth.init();
}

export function initLangFactory() {
  const lang = inject(LanguageService);
  return () => Promise.resolve(); // LanguageService applies in constructor
}

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(APP_ROUTES),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(BrowserAnimationsModule),
    importProvidersFrom(TranslateModule.forChild()),
    provideTranslateService({
      defaultLanguage: 'ar',
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json',
      }),
    }),
    LanguageService,
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: 'false',
        },
      },
    }),
    MessageService,
    ConfirmationService,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: APP_INITIALIZER, useFactory: initAuthFactory, multi: true },
    { provide: APP_INITIALIZER, useFactory: initLangFactory, multi: true },
  ],
}).catch((err) => console.error(err));
